import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { getI18nLanguage } from 'app/lib/language';
import { useAllCategories } from 'app/network/category';
import { type Tag, useAllTags, useDefaultListingLocation } from 'app/network/listing';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Category } from 'app/types/category';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconAdjustments } from '@tabler/icons-react-native';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, useWindowDimensions } from 'react-native';
import { GuestLocationFilterContent } from './guest-location-filter-content';
import { useGuestLocationDraft } from './use-guest-location-draft';

const theme = createDSTheme();

interface GuestMobileListingsFiltersProperties {
  navigation: SpecificNavigationProp<'all-listings'>;
  route: SpecificRouteProp<'all-listings'>;
}

export function GuestMobileListingsFilters({ navigation, route }: GuestMobileListingsFiltersProperties) {
  const { t } = useTranslation('all-listings');
  const { height: windowHeight } = useWindowDimensions();
  const [open, setOpen] = useState(false);
  const { data: categories } = useAllCategories();
  const { data: tags } = useAllTags(true);
  const { data: defaultLocation } = useDefaultListingLocation();

  const activeCategory = categories?.find((category) => category.id === route.params?.categoryId);

  const activeTag = tags?.find((tag) => tag.id === route.params?.tagId);
  const usesCustomLocation = Boolean(
    defaultLocation &&
    route.params?.searchCenterLatitude !== undefined &&
    route.params.searchCenterLongitude !== undefined &&
    (route.params.searchCenterLatitude !== defaultLocation.latitude ||
      route.params.searchCenterLongitude !== defaultLocation.longitude ||
      route.params.searchDistance !== defaultLocation.searchDistance),
  );
  const numFilters = Number(Boolean(activeCategory)) + Number(Boolean(activeTag)) + Number(usesCustomLocation);
  const close = () => setOpen(false);
  const [draftCategoryId, setDraftCategoryId] = useState<string>();
  const [draftTagId, setDraftTagId] = useState<string>();
  const locationDraft = useGuestLocationDraft();
  const draftLocation = {
    address: locationDraft.draftAddress,
    distance: locationDraft.draftDistance,
    location: locationDraft.draftLocation,
  };
  const [locationDraftKey, setLocationDraftKey] = useState(0);

  const openFilters = () => {
    setDraftCategoryId(activeCategory?.id);
    setDraftTagId(activeTag?.id);
    locationDraft.resetToApplied();
    setLocationDraftKey((key) => key + 1);
    setOpen(true);
  };

  const reset = () => {
    setDraftCategoryId(undefined);
    setDraftTagId(undefined);
    if (defaultLocation) {
      locationDraft.setDraftAddress('');
      locationDraft.setDraftDistance(defaultLocation.searchDistance);
      locationDraft.setDraftLocation({ latitude: defaultLocation.latitude, longitude: defaultLocation.longitude });
    } else {
      locationDraft.resetToApplied();
    }
    setLocationDraftKey((key) => key + 1);
  };

  const apply = () => {
    if (!draftLocation.location) return;

    navigation.setParams({
      categoryId: draftCategoryId,
      tagId: draftTagId,
      searchCenterLatitude: draftLocation.location.latitude,
      searchCenterLongitude: draftLocation.location.longitude,
      searchDistance: draftLocation.distance,
      searchLocationLabel: draftLocation.address.trim() || undefined,
    });
    close();
  };

  return (
    <>
      <Column>
        <Pressable
          accessibilityLabel={t('filters.name')}
          hitSlop={10}
          onPress={openFilters}
          style={{
            alignItems: 'center',
            backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
            borderRadius: theme.spacing['spacing.general.x2'],
            height: 40,
            justifyContent: 'center',
            width: 40,
          }}
        >
          <IconAdjustments color={theme.colors['text.primary']} size={26} strokeWidth={ICON_STROKE_WIDTH} />
        </Pressable>
        {numFilters > 0 ? (
          <Column
            alignItems="center"
            backgroundColor="surface.brand.default"
            borderRadius="radius.xl"
            height={16}
            justifyContent="center"
            position="absolute"
            right={-4}
            top={-4}
            width={16}
          >
            <Text color="surface.default" fontSize={10} fontWeight="bold">
              {numFilters}
            </Text>
          </Column>
        ) : null}
      </Column>

      <BottomSheet onClose={close} open={open} showIndicator>
        <Column style={{ maxHeight: windowHeight * 0.8 }}>
          <ScrollView keyboardShouldPersistTaps="handled" style={{ flexShrink: 1 }}>
            <Column gap="spacing.general.x6" paddingBottom="spacing.general.x6" paddingHorizontal="spacing.general.x5">
              <GuestLocationFilterContent
                key={locationDraftKey}
                draftState={locationDraft}
                navigation={navigation}
                showActions={false}
              />
              <GuestCategoryFilter activeId={draftCategoryId} onChange={setDraftCategoryId} />
              <GuestTagFilter activeId={draftTagId} onChange={setDraftTagId} />
            </Column>
          </ScrollView>
          <Column
            backgroundColor="surface.default"
            borderTopColor="border.divider"
            borderTopWidth={1}
            gap="spacing.general.x2"
            padding="spacing.general.x5"
          >
            <Button
              disabled={!draftLocation.location}
              onPress={apply}
              style={{ minHeight: 48, width: '100%' }}
              variant="primary"
            >
              <Text color="white" fontSize={16} fontWeight="700">
                {t('guest.apply')}
              </Text>
            </Button>
            <Button onPress={reset} style={{ minHeight: 44, width: '100%' }} variant="plain">
              <Text color="text.brand" fontSize={16} fontWeight="600">
                {t('guest.reset')}
              </Text>
            </Button>
          </Column>
        </Column>
      </BottomSheet>
    </>
  );
}

function GuestCategoryFilter({
  activeId,
  onChange,
}: {
  activeId?: Category['id'];
  onChange: (id: Category['id'] | undefined) => void;
}) {
  const { data: categories } = useAllCategories();
  const { t } = useTranslation('all-listings');
  const language = getI18nLanguage();

  return (
    <GuestChipFilter
      activeId={activeId}
      items={categories?.map((category) => ({ id: category.id, label: category.name[language] })) ?? []}
      onChange={onChange}
      title={t('guest.categories')}
    />
  );
}

function GuestTagFilter({
  activeId,
  onChange,
}: {
  activeId?: Tag['id'];
  onChange: (id: Tag['id'] | undefined) => void;
}) {
  const { data: tags } = useAllTags(true);
  const { t } = useTranslation('all-listings');

  return (
    <GuestChipFilter
      activeId={activeId}
      items={tags?.map((tag) => ({ id: tag.id, label: tag.tagName })) ?? []}
      onChange={onChange}
      title={t('guest.tags')}
    />
  );
}

function GuestChipFilter({
  activeId,
  items,
  onChange,
  title,
}: {
  activeId?: string;
  items: { id: string; label: string }[];
  onChange: (id: string | undefined) => void;
  title: string;
}) {
  const { t } = useTranslation('all-listings');

  return (
    <Column gap="spacing.general.x4">
      <Text fontSize={18} fontWeight="700">
        {title}
      </Text>
      <ScrollView showsVerticalScrollIndicator style={{ maxHeight: 258 }}>
        <Row flexWrap="wrap" style={{ gap: theme.spacing['spacing.general.x2'] }}>
          <GuestFilterChip label={t('filters.all')} onPress={() => onChange(undefined)} selected={!activeId} />
          {items.map((item) => (
            <GuestFilterChip
              key={item.id}
              label={item.label}
              onPress={() => onChange(item.id)}
              selected={item.id === activeId}
            />
          ))}
        </Row>
      </ScrollView>
    </Column>
  );
}

function GuestFilterChip({ label, onPress, selected }: { label: string; onPress: () => void; selected: boolean }) {
  return (
    <Button
      onPress={onPress}
      style={{
        backgroundColor: selected ? theme.colors['surface.inverse.default'] : theme.colors['surface.secondary-neutral'],
        borderRadius: theme.borderRadii['radius.full'],
        minHeight: 36,
        paddingHorizontal: 12,
        paddingVertical: 8,
      }}
      variant="plain"
    >
      <Text color={selected ? 'text.inverse.primary' : 'text.primary'} fontSize={14} fontWeight="600">
        {label}
      </Text>
    </Button>
  );
}
